import { Button, Dialog, Flex, Heading, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import Countdown from "react-countdown";
import { useEffect, useState } from "react";
import { useCheckout } from "./checkout/checkout-context";
import { useIdle } from "react-use";

type InactivityAlertProps = {
  onContinue: () => void;
  idleTime: number;
};

export function InactivityAlert({ onContinue, idleTime }: InactivityAlertProps) {
  const AUTO_CLOSE_TIME = 1000 * 10; // 10 seconds
  const [closeDate] = useState(() => Date.now() + AUTO_CLOSE_TIME);
  const { closeSession, isCloseLoading } = useCheckout();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col w-full items-center self-stretch p-8">
      <div className="flex-grow">
        <figure className="flex justify-center">
          <img src="/alert.png" className="w-auto h-[100px]" />
        </figure>
        <Heading size="6" mb="2" align="center" weight="bold" color="gray" highContrast>
          {t("inactivityAlertTitle")}
        </Heading>
        <Flex gap="2" mb="2" justify="center">
          <Text size="4" color="gray">
            {t("inactivityAlertDescription", { minutes: Math.floor(idleTime / 1000 / 60) })}
          </Text>
        </Flex>
      </div>
      <Flex className="w-full" direction="column" justify="between" gap="2" mt="4">
        <Button
          disabled={isCloseLoading}
          className="w-full !relative !overflow-hidden"
          size="4"
          onClick={() => {
            onContinue();
          }}
        >
          <span className="relative z-20">{t("continueCheckout")}</span>
        </Button>
        <Countdown
          date={closeDate}
          onComplete={closeSession}
          renderer={({ seconds }) => (
            <Text color="gray" align="center" className="tabular-nums">
              {t("autoCancelCountdown", { seconds })}
            </Text>
          )}
        ></Countdown>
      </Flex>
    </div>
  );
}

const DEFAULT_IDLE_TIME = 1000 * 60 * 3; // 3 minutes

export function InactivityAlertModal() {
  const IDLE_TIME = import.meta.env.VITE_INACTIVITY_ALERT_TIME
    ? Number(import.meta.env.VITE_INACTIVITY_ALERT_TIME)
    : DEFAULT_IDLE_TIME;
  const [open, setOpen] = useState(false);
  const [idleTime, setIdleTime] = useState(IDLE_TIME);
  const isIdle = useIdle(idleTime);

  useEffect(() => {
    if (isIdle) {
      setOpen(true);
      setIdleTime(Infinity);
    }
  }, [isIdle]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          setOpen(false);
          setIdleTime(IDLE_TIME);
        }
      }}
    >
      <Dialog.Content maxWidth="492px">
        {open && (
          <InactivityAlert
            idleTime={IDLE_TIME}
            onContinue={() => {
              setOpen(false);
              setIdleTime(IDLE_TIME);
            }}
          />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
